import React, { useEffect, useRef } from 'react';

import useMountAnimation from '../../../../hooks/useMountAnimation';
import { Slide, type StepSlideDir } from './MobileStepSlide.styles';

/**
 * Module-level memory of the last step index. Each step keys a fresh mount of this
 * wrapper, so the new instance has no previous state to diff against — the module
 * memory lets it still tell forward (a later step) from back (an earlier step).
 * Starts at 0 (the intro), so the FIRST step — country — slides IN forward as the
 * morph dismisses the intro, rather than just fading.
 */
let lastStepIndex = 0;

/** Reset the slide memory to the intro (step 0), so the NEXT step to mount —
 *  country — slides IN forward again rather than fading. Called by `MobileApp`
 *  when the intro re-opens (back), keeping every dismiss a forward slide. */
export const resetStepSlideToIntro = (): void => {
  lastStepIndex = 0;
};

/**
 * Slides the active wizard step's body IN on each step change: forward
 * navigations arrive from the right, back from the left. Slide-in only (the
 * outgoing step unmounts with its route), matching the sibling configurator. The
 * progress rail lives in the fixed top bar OUTSIDE this wrapper, so the intro→rail
 * morph floats above the sliding body untouched.
 *
 * Mount it with `key={step}` so each step change remounts it and replays the
 * entrance (the chrome around it persists, so only the body slides).
 */
export const MobileStepSlide = ({
  step,
  children,
}: {
  step: number;
  children: React.ReactNode;
}): React.ReactElement => {
  // Direction is decided once at this mount's first render — before the effect
  // advances the module memory — and frozen for the step's life, so an in-step
  // re-render never replays the entrance.
  const dirRef = useRef<StepSlideDir | null>(null);
  if (dirRef.current === null) {
    dirRef.current = step === lastStepIndex ? 'none' : step > lastStepIndex ? 'forward' : 'back';
  }

  // First paint at the travel offset (opacity 0), then flip to rest next frame so
  // the CSS transition animates it in — the area's standard entrance pattern.
  const entered = useMountAnimation();

  useEffect(() => {
    lastStepIndex = step;
  }, [step]);

  return (
    <Slide $dir={dirRef.current} $entered={entered}>
      {children}
    </Slide>
  );
};

export default MobileStepSlide;
